import {
  incidentApiRef,
  IncidentRestApi,
} from '@internal/backstage-plugin-neo4j-incidents-react';
import {
  trafficConfigApiRef,
  TrafficConfigRestApi,
} from '@internal/backstage-plugin-neo4j-networking-react';
import {
  ApiBlueprint,
  configApiRef,
  createApiFactory,
  createFrontendPlugin,
  discoveryApiRef,
  fetchApiRef,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import { backendAuthApiRef } from '@internal/plugin-neo4j-auth-common';
import { mdiDatabase } from '@mdi/js';
import Icon from '@mdi/react';

import { NEO4J_SERVERS_BASE_URL, AURA_OPERATIONS_BASE_URL } from './const';
import {
  rootRouteRef,
  clustersListRouteRef,
  clusterDetailRouteRef,
  databaseDetailRouteRef,
  serverDetailRouteRef,
  auraOperationsRouteRef,
} from './routes';
import {
  auraOperationsApiRef,
  AuraOperationsRestApi,
} from '@internal/backstage-plugin-neo4j-aura-operations-react';

import { databaseManagerApiRef, DatabaseManagerRestApi } from './apis';

// ── API (→ global API registry) ─────────────────────────
const databaseManagerApi = ApiBlueprint.make({
  name: 'database-manager-api',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: databaseManagerApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          fetchApi: fetchApiRef,
          config: configApiRef,
          backendAuthApi: backendAuthApiRef,
        },
        factory: ({ discoveryApi, fetchApi, config, backendAuthApi }) =>
          new DatabaseManagerRestApi({
            discoveryApi,
            fetchApi,
            config,
            backendAuthApi,
          }),
      }),
    ),
});

const trafficConfigApi = ApiBlueprint.make({
  name: 'traffic-config-api',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: trafficConfigApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          fetchApi: fetchApiRef,
        },
        factory: ({ discoveryApi, fetchApi }) =>
          new TrafficConfigRestApi({
            discoveryApi,
            fetchApi,
          }),
      }),
    ),
});

const incidentApi = ApiBlueprint.make({
  name: 'incident-api',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: incidentApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          fetchApi: fetchApiRef,
        },
        factory: ({ discoveryApi, fetchApi }) =>
          new IncidentRestApi({ discoveryApi, fetchApi }),
      }),
    ),
});

const auraOperationsApi = ApiBlueprint.make({
  name: 'aura-operations-api',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: auraOperationsApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          fetchApi: fetchApiRef,
          backendAuthApi: backendAuthApiRef,
        },
        factory: ({ discoveryApi, fetchApi, backendAuthApi }) =>
          new AuraOperationsRestApi({ discoveryApi, fetchApi, backendAuthApi }),
      }),
    ),
});

// ── Routes (→ app/routes) ───────────────────────────────
const clustersListPage = PageBlueprint.make({
  name: 'clusters-list',
  params: {
    title: 'Neo4j Clusters',
    icon: <Icon path={mdiDatabase} size="20px" />,
    path: '/neo4j-clusters',
    routeRef: clustersListRouteRef,
    noHeader: true,
    loader: () =>
      import('./pages/Neo4jClustersListPage').then(m => (
        <m.Neo4jClustersListPage />
      )),
  },
});

const clusterDetailPage = PageBlueprint.make({
  name: 'cluster-detail',
  params: {
    path: '/neo4j-clusters/:neo4jClusterId',
    routeRef: clusterDetailRouteRef,
    noHeader: true,
    loader: () =>
      import('./pages/Neo4jClusterPage').then(m => <m.Neo4jClusterPage />),
  },
});

const databasesListPage = PageBlueprint.make({
  params: {
    title: 'Neo4j Databases',
    icon: <Icon path={mdiDatabase} size="20px" />,
    path: '/neo4j-databases',
    routeRef: rootRouteRef,
    noHeader: true,
    loader: () =>
      import('./pages/DatabasesListPage').then(m => <m.DatabasesListPage />),
  },
});

const databaseDetailPage = PageBlueprint.make({
  name: 'database-detail',
  params: {
    path: '/neo4j-databases/:neo4jDatabaseId',
    routeRef: databaseDetailRouteRef,
    noHeader: true,
    loader: () =>
      import('./pages/Neo4jDatabasePage').then(m => <m.Neo4jDatabasePage />),
  },
});

const serverDetailPage = PageBlueprint.make({
  name: 'server-detail',
  params: {
    path: `${NEO4J_SERVERS_BASE_URL}/:serverId/databases`,
    routeRef: serverDetailRouteRef,
    noHeader: true,
    loader: () =>
      import('./pages/Neo4jServerPage').then(m => <m.Neo4jServerPage />),
  },
});

const auraOperationsPage = PageBlueprint.make({
  name: 'aura-operations',
  params: {
    title: 'Aura Operations',
    path: AURA_OPERATIONS_BASE_URL,
    routeRef: auraOperationsRouteRef,
    noHeader: true,
    loader: () =>
      import('./pages/AuraOperationsPage').then(m => <m.AuraOperationsPage />),
  },
});

export default createFrontendPlugin({
  info: {
    packageJson: () => import('../package.json'),
    manifest: () => import('../catalog-info.yaml'),
  },
  pluginId: 'neo4j-databases',
  extensions: [
    auraOperationsApi,
    auraOperationsPage,
    clusterDetailPage,
    clustersListPage,
    databaseDetailPage,
    databaseManagerApi,
    databasesListPage,
    incidentApi,
    serverDetailPage,
    trafficConfigApi,
  ],
  routes: {
    clusterDetail: clusterDetailRouteRef,
    clustersList: clustersListRouteRef,
    databaseDetail: databaseDetailRouteRef,
    root: rootRouteRef,
    serverDetail: serverDetailRouteRef,
    auraOperations: auraOperationsRouteRef,
  },
});
